import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SnackbarContext } from "../../../config/hooks/use-toast";
import {
  getPagedUsersService,
  updateUserService,
  deleteUserService,
} from "../../../services/user-service";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Modal,
  Box,
} from "@mui/material";
import { classesLevel } from "../../../mocks/education-level";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupsIcon from "@mui/icons-material/Groups";
import SchoolIcon from "@mui/icons-material/School";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faEye, 
  faPen, 
  faTrash, 
  faSearch, 
  faUserShield, 
  faUsers,
  faRefresh,
  faUserGraduate,
  faChalkboardTeacher,
  faUserTie,
  faGraduationCap,
  faCheckCircle,
  faTimesCircle,
  faToggleOn,
  faUserGear, 
  faPlus, 
  faCheck,
  faEdit,
  faCircleCheck,
  faTrashCan,
  faTriangleExclamation,
  faToggleOff,
  faPhone,
  faEnvelope,
  faUser,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import "./management-users.css";

interface User {
  id: number;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  role?: { name: string } | string;
  educationLevel?: string;
  enabled?: boolean;
  isEnabled?: boolean;
  status?: string;
  createdAt?: string;
  subjectCount?: number;
  subjects?: any[];
  classCount?: number;
  teacherCount?: number;
  studentCount?: number;
  teachers?: any[];
  students?: any[];
  groups?: any[];
  endDate?: string;
  isSubscriptionActive?: boolean;
}


const ManagementUsers: React.FC = () => {
  const snackbarContext = useContext(SnackbarContext);
  const [users, setUsers] = useState<User[]>([]);
  const [totals, setTotals] = useState({ total: 0, students: 0, teachers: 0, superTeachers: 0, admins: 0 });
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState<any>({});
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [educationFilter, setEducationFilter] = useState("");
  const [sortCreatedAt, setSortCreatedAt] = useState<"asc" | "desc">("desc");
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const usersPerPage = 20;
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        search: query.trim(),
        role: roleFilter,
        status: statusFilter,
        education: educationFilter,
      }),
    [query, roleFilter, statusFilter, educationFilter]
  );
  const lastFiltersRef = useRef(filtersKey);

  // Helper function to get role icon
  const getRoleIcon = (role: string) => {
    switch (role) {
      case "ROLE_ADMIN":
        return faUserTie;
      case "ROLE_SUPER_TEACHER":
        return faChalkboardTeacher;
      case "ROLE_TEACHER":
        return faGraduationCap;
      case "ROLE_STUDENT":
        return faUserGraduate;
      default:
        return faUserShield;
    }
  };

  // Helper function to get role color
  const getRoleColor = (role: string) => {
    switch (role) {
      case "ROLE_ADMIN":
        return "bg-red-100 text-red-800 border-red-200";
      case "ROLE_SUPER_TEACHER":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "ROLE_TEACHER":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "ROLE_STUDENT":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Helper function to get role display name
  const getRoleDisplay = (role: string) => {
    switch (role) {
      case "ROLE_STUDENT":
        return "Eleve";
      case "ROLE_TEACHER":
        return "Professeur";
      case "ROLE_SUPER_TEACHER":
        return "Super Professeur";
      case "ROLE_ADMIN":
        return "Admin";
      default:
        return role.replace('ROLE_', '').replace('_', ' ');
    }
  };

  // Helper function to get user avatar
  const getUserInitials = (fullNameOrFirst?: string, lastNameOpt?: string) => {
    if (lastNameOpt !== undefined) {
      // Classic usage: getUserInitials(firstName, lastName)
      const first = fullNameOrFirst?.charAt(0)?.toUpperCase() || "";
      const last = lastNameOpt?.charAt(0)?.toUpperCase() || "";
      return `${first}${last}` || "U";
    }
    // If only one argument, treat as fullName
    if (fullNameOrFirst) {
      const parts = fullNameOrFirst.trim().split(" ");
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return "U";
  };


  // Helper function to get user status
  const getUserStatus = (user: User) => {
    if (typeof user.isEnabled === 'boolean') {
      return user.isEnabled;
    }
    if (typeof user.enabled === 'boolean') {
      return user.enabled;
    }
    if (user.status !== undefined) {
      return user.status === 'active' || user.status === 'enabled';
    }
    return true;
  };

  const getRoleName = (user?: User | null) => {
    return typeof user?.role === "string" ? user.role : user?.role?.name || "";
  };

  // Helper function to get status color and text
  const getStatusDisplay = (isEnabled: boolean) => {
    return {
      color: isEnabled ? "text-green-600" : "text-red-600",
      bgColor: isEnabled ? "bg-green-100" : "bg-red-100",
      borderColor: isEnabled ? "border-green-200" : "border-red-200",
      icon: isEnabled ? faCheckCircle : faTimesCircle,
      text: isEnabled ? "Activé" : "Désactivé"
    };
  };

  const requestIdRef = useRef(0);

  const fetchUsers = async (page = 1) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      const zeroBasedPage = Math.max(page - 1, 0);
      const statusValue = statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined;
      const res = await getPagedUsersService(zeroBasedPage, usersPerPage, {
        search: query.trim(),
        role: roleFilter || undefined,
        isEnabled: statusValue,
        educationLevel: educationFilter || undefined,
      });
      const payload = res?.data ?? res;
      const payloadData = payload?.data ?? payload;
      const rawList = payloadData?.content || [];
      const list = rawList.map((user: any) => ({
        ...user,
        phoneNumber: user.phoneNumber || user.phone || user.telephone || user.tel || "",
        isEnabled: typeof user.isEnabled === 'boolean' ? user.isEnabled : (typeof user.enabled === 'boolean' ? user.enabled : undefined)
      }));
      setUsers(list);
      setTotalPages(payloadData?.totalPages ?? 1);
      const totalStudents = payloadData?.totalStudents ?? 0;
      const totalTeachers = payloadData?.totalTeachers ?? 0;
      const totalSuperTeachers = payloadData?.totalSuperTeachers ?? 0;
      const totalAdmins = payloadData?.totalAdmins ?? 0;
      
      // Total is the sum of all roles
      const overallTotal = totalStudents + totalTeachers + totalSuperTeachers + totalAdmins;
      
      setTotals({
        total: overallTotal > 0 ? overallTotal : payloadData?.totalElements ?? list.length,
        students: totalStudents,
        teachers: totalTeachers,
        superTeachers: totalSuperTeachers,
        admins: totalAdmins,
      });
      setCurrentPage(prev => {
        const serverPage = (payloadData?.pageNumber ?? zeroBasedPage) + 1;
        return prev === serverPage ? prev : serverPage;
      });
    } catch (e) {
      console.error(e);
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
      snackbarContext?.showMessage?.("Error", "Failed to fetch users", "error");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const filtersChanged = lastFiltersRef.current !== filtersKey;
    if (filtersChanged) {
      lastFiltersRef.current = filtersKey;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
    }
    const delay = query ? 350 : 0;
    fetchTimerRef.current = setTimeout(() => {
      fetchUsers(currentPage);
    }, delay);
    return () => {
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filtersKey, query]);

  const openView = (user: User) => {
  console.log('User object on view:', user);
  setSelectedUser(user);
  setViewOpen(true);
  };

  const closeView = () => {
    setSelectedUser(null);
    setViewOpen(false);
  };

  const handleEditOpen = (user: User) => {
    setSelectedUser(user);
    const userRole = typeof user.role === "string" ? user.role : user.role?.name || "";
    setForm({
      fullName: user.fullName || "",
      email: user.email || "",
      // Only set educationLevel for non-teacher roles
      ...(userRole !== "ROLE_TEACHER" && userRole !== "ROLE_SUPER_TEACHER" ? { educationLevel: user.educationLevel || "" } : {}),
      isEnabled: typeof user.isEnabled === 'boolean' ? user.isEnabled : getUserStatus(user),
    phoneNumber: user.phoneNumber || "",
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setSelectedUser(null);
    setForm({});
    setEditOpen(false);
  };

  const handleDeleteOpen = (user: User) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setSelectedUser(null);
    setDeleteOpen(false);
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    try {
      const { enabled, ...rest } = form;
      // Remove educationLevel for teacher/super teacher
      const userRole = typeof selectedUser.role === "string" ? selectedUser.role : selectedUser.role?.name || "";
      let payload = { ...rest };
      if (userRole === "ROLE_TEACHER" || userRole === "ROLE_SUPER_TEACHER") {
        const { educationLevel, ...withoutEducation } = payload;
        payload = withoutEducation;
      }
      const updatedUser = await updateUserService(selectedUser.id, payload);
      snackbarContext?.showMessage?.("Success", "User updated", "success");
      // Update local state instantly
      setUsers(prev => {
        const newList = prev.map(u => u.id === selectedUser.id ? { ...u, ...payload } : u);
        return newList;
      });
      handleEditClose();
    } catch (e) {
      console.error(e);
      snackbarContext?.showMessage?.("Error", "Failed to update user", "error");
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    try {
      await deleteUserService(selectedUser.id);
      snackbarContext?.showMessage?.("Success", "User deleted", "success");
      handleDeleteClose();
      fetchUsers(currentPage);
    } catch (e) {
      console.error(e);
      snackbarContext?.showMessage?.("Error", "Failed to delete user", "error");
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const currentStatus = getUserStatus(user);
      const newStatus = !currentStatus;
      // Update the user status using isEnabled param
      await updateUserService(user.id, {
        ...user,
        isEnabled: newStatus
      });
      snackbarContext?.showMessage?.("Success", 
        `Utilisateur ${newStatus ? 'activé' : 'désactivé'}`,
        "success"
      );
      await fetchUsers(currentPage);
    } catch (e) {
      console.error(e);
      snackbarContext?.showMessage?.("Error", "Failed to update user status", "error");
    }
  };

  const filtered = useMemo(() => {
    return users.slice().sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortCreatedAt === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [users, sortCreatedAt]);

    // Pagination logic with server pagination
  const paginatedUsers = useMemo(() => filtered, [filtered]);

  // Get unique roles for filter dropdown
  const availableRoles = useMemo(() => {
    const roles = users.map((u) => typeof u.role === "string" ? u.role : u.role?.name || "");
    const defaultRoles = ["ROLE_STUDENT", "ROLE_TEACHER", "ROLE_SUPER_TEACHER", "ROLE_ADMIN"];
    const uniqueRoles = Array.from(new Set([...roles, ...defaultRoles])).filter(Boolean);
    return uniqueRoles;
  }, [users]);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 management-users-container">
      <div className="mx-auto max-w-7xl">
        {/* Header Section - Premium */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <h1 className="text-4xl font-black text-title uppercase tracking-tight">
              Gestion Utilisateurs
            </h1>
            <p className="text-[11px] font-bold text-text/40 uppercase tracking-[0.3em]">
              Administration & Contrôle d'accès
            </p>
          </div>
          <div className="flex items-center gap-3">
             <button
                onClick={() => fetchUsers(currentPage)}
                disabled={loading}
                className="group p-4 bg-white/70 backdrop-blur-xl border border-white rounded-2xl shadow-xl shadow-black/5 hover:bg-white transition-all duration-300"
              >
                <RefreshIcon className={`w-5 h-5 text-primary transition-transform duration-500 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`} />
              </button>
          </div>
        </div>

        {/* Stats Section - Ultra Premium */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {[
            { label: "Utilisateurs", value: totals.total, icon: faUsers, color: "blue", gradient: "from-[#3B82F6] to-[#2563EB]", shadow: "shadow-blue-500/20" },
            { label: "Élèves", value: totals.students, icon: faUserGraduate, color: "emerald", gradient: "from-[#10B981] to-[#059669]", shadow: "shadow-emerald-500/20" },
            { label: "Professeurs", value: totals.teachers + totals.superTeachers, icon: faChalkboardTeacher, color: "purple", gradient: "from-[#8B5CF6] to-[#7C3AED]", shadow: "shadow-purple-500/20" },
            { label: "Administration", value: totals.admins, icon: faUserShield, color: "rose", gradient: "from-[#F43F5E] to-[#E11D48]", shadow: "shadow-rose-500/20" },
          ].map((stat, i) => (
            <div key={i} className="group relative bg-white/60 backdrop-blur-2xl rounded-[3rem] border border-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] p-8 transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_40px_80px_-15px_rgba(0,0,0,0.1)] hover:bg-white">
              <div className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br ${stat.gradient} opacity-5 blur-3xl group-hover:opacity-10 transition-opacity`}></div>
              
              <div className="flex flex-col gap-6">
                <div className={`w-16 h-16 rounded-[1.5rem] bg-gradient-to-br ${stat.gradient} flex items-center justify-center text-white shadow-2xl ${stat.shadow} transition-transform duration-700 group-hover:rotate-[10deg] group-hover:scale-110`}>
                  <FontAwesomeIcon icon={stat.icon} className="text-2xl" />
                </div>
                
                <div>
                  <div className="flex items-baseline gap-2">
                    <h4 className="text-5xl font-black text-title tracking-tighter tabular-nums">{stat.value}</h4>
                  </div>
                  <p className="text-[10px] font-black text-text/30 uppercase tracking-[0.3em] mt-1">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search and Filter Section - Glassmorphic */}
        <div className="bg-white/70 backdrop-blur-xl rounded-[2.5rem] border border-white shadow-2xl shadow-black/5 p-8 mb-12">
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Search */}
              <div className="relative group lg:col-span-1">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text/30 group-focus-within:text-primary transition-colors">
                    <SearchIcon className="w-5 h-5" />
                 </div>
                 <input
                    type="text"
                    placeholder="NOM OU EMAIL..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full h-14 pl-12 pr-4 bg-gray-50 border border-gray-100 focus:bg-white focus:border-primary/20 rounded-2xl font-black text-[11px] uppercase tracking-wider outline-none transition-all"
                 />
              </div>

              {/* Sort */}
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text/30 group-focus-within:text-primary transition-colors">
                    <FilterListIcon className="w-5 h-5" />
                 </div>
                 <select
                    value={sortCreatedAt}
                    onChange={e => setSortCreatedAt(e.target.value as "asc" | "desc")}
                    className="w-full h-14 pl-12 pr-4 bg-gray-50 border border-gray-100 focus:bg-white focus:border-primary/20 rounded-2xl font-black text-[11px] uppercase tracking-wider outline-none appearance-none cursor-pointer transition-all"
                 >
                    <option value="desc">PLUS RÉCENT</option>
                    <option value="asc">PLUS ANCIEN</option>
                 </select>
              </div>

              {/* Role Filter */}
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text/30 group-focus-within:text-primary transition-colors">
                    <PersonIcon className="w-5 h-5" />
                 </div>
                 <select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    className="w-full h-14 pl-12 pr-4 bg-gray-50 border border-gray-100 focus:bg-white focus:border-primary/20 rounded-2xl font-black text-[11px] uppercase tracking-wider outline-none appearance-none cursor-pointer transition-all"
                 >
                    <option value="">TOUS LES RÔLES</option>
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>{getRoleDisplay(role).toUpperCase()}</option>
                    ))}
                 </select>
              </div>

              {/* Status Filter */}
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text/30 group-focus-within:text-primary transition-colors">
                    <CheckCircleIcon className="w-5 h-5" />
                 </div>
                 <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="w-full h-14 pl-12 pr-4 bg-gray-50 border border-gray-100 focus:bg-white focus:border-primary/20 rounded-2xl font-black text-[11px] uppercase tracking-wider outline-none appearance-none cursor-pointer transition-all"
                 >
                    <option value="">STATUT: TOUS</option>
                    <option value="active">ACTIVÉ</option>
                    <option value="inactive">DÉSACTIVÉ</option>
                 </select>
              </div>

              {/* Level Filter */}
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text/30 group-focus-within:text-primary transition-colors">
                    <SchoolIcon className="w-5 h-5" />
                 </div>
                 <select
                    value={educationFilter}
                    onChange={e => setEducationFilter(e.target.value)}
                    className="w-full h-14 pl-12 pr-4 bg-gray-50 border border-gray-100 focus:bg-white focus:border-primary/20 rounded-2xl font-black text-[11px] uppercase tracking-wider outline-none appearance-none cursor-pointer transition-all"
                 >
                    <option value="">TOUS LES NIVEAUX</option>
                    {classesLevel.map((level) => (
                      <option key={level.value} value={level.value}>{level.label.toUpperCase()}</option>
                    ))}
                 </select>
              </div>
           </div>
        </div>

        {/* Users Table Card - High Fidelity */}
        <div className="bg-white/70 backdrop-blur-xl rounded-[2.5rem] border border-white shadow-2xl shadow-black/5 overflow-hidden mb-12">
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead className="bg-[#f8fafc]/50 sticky top-0 z-20 backdrop-blur-md">
                <tr>
                  <th className="px-8 py-6 text-[10px] font-black text-text/40 uppercase tracking-[0.2em] border-b border-gray-100/50">#</th>
                  <th className="px-8 py-6 text-[10px] font-black text-text/40 uppercase tracking-[0.2em] border-b border-gray-100/50">Utilisateur</th>
                  <th className="px-8 py-6 text-[10px] font-black text-text/40 uppercase tracking-[0.2em] border-b border-gray-100/50">Type / Niveau</th>
                  <th className="px-8 py-6 text-[10px] font-black text-text/40 uppercase tracking-[0.2em] border-b border-gray-100/50">Abonnement</th>
                  <th className="px-8 py-6 text-[10px] font-black text-text/40 uppercase tracking-[0.2em] border-b border-gray-100/50 text-center">Statut</th>
                  <th className="px-8 py-6 text-[10px] font-black text-text/40 uppercase tracking-[0.2em] border-b border-gray-100/50 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-8 py-20 text-center">
                      <CircularProgress size={40} thickness={5} style={{ color: '#09745f' }} />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-8 py-20 text-center font-black text-[11px] text-text/20 uppercase tracking-widest">Aucun utilisateur trouvé</td>
                  </tr>
                ) : paginatedUsers.map((user, index) => {
                  const role = typeof user.role === "string" ? user.role : user.role?.name || "";
                  const isEnabled = getUserStatus(user);
                  const isStudent = role === "ROLE_STUDENT";
                  const status = getStatusDisplay(isEnabled);
                  
                  return (
                    <tr key={user.id} className="group hover:bg-white/50 transition-colors duration-300">
                      <td className="px-8 py-6 font-bold text-[11px] text-text/20">
                        {String((currentPage - 1) * usersPerPage + index + 1).padStart(2, '0')}
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <Avatar 
                              className="w-12 h-12 rounded-[1.2rem] border-2 border-white shadow-xl shadow-black/5 bg-gradient-to-br from-primary/20 to-primary/5 text-primary text-sm font-black"
                            >
                              {getUserInitials(user.fullName || "")}
                            </Avatar>
                            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${isEnabled ? 'bg-[#10b981]' : 'bg-[#f43f5e]'}`}></div>
                          </div>
                          <div>
                            <p className="font-black text-[13px] text-text tracking-tight uppercase">{(user.fullName || "-").trim()}</p>
                            <p className="text-[10px] font-bold text-text/40 lowercase tracking-wide">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                         <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                               <div className={`p-2 rounded-lg ${getRoleColor(role)} bg-opacity-10`}>
                                  <FontAwesomeIcon icon={getRoleIcon(role)} className="text-[10px]" />
                               </div>
                               <span className="text-[11px] font-black uppercase tracking-wider text-title">
                                  {getRoleDisplay(role)}
                               </span>
                            </div>
                            {isStudent && user.educationLevel && (
                                <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary/5 rounded-md border border-primary/10">
                                   <span className="text-[8px] font-black text-primary tracking-[0.1em] uppercase">
                                     {classesLevel.find(l => l.value === user.educationLevel)?.label || user.educationLevel}
                                   </span>
                                </div>
                            )}
                         </div>
                      </td>
                      <td className="px-8 py-6">
                        {isStudent ? (
                          <div className="space-y-1.5">
                            {user.endDate ? (() => {
                              const isSubActive = new Date(user.endDate) >= new Date();
                              return (
                                <>
                                  <div className={`inline-flex items-center justify-center h-8 px-4 rounded-xl font-black text-[9px] uppercase tracking-widest shadow-sm transition-all group-hover:scale-105 ${
                                    isSubActive 
                                      ? 'bg-emerald-500 text-white shadow-emerald-200' 
                                      : 'bg-rose-500 text-white shadow-rose-200 opacity-60'
                                  }`}>
                                    {isSubActive ? 'Abonnement Actif' : 'Abonnement Expiré'}
                                  </div>
                                  <p className="text-[9px] font-bold text-text/30 uppercase tracking-tighter pl-1">
                                    jusqu'au <span className="text-text/60 font-black">{new Date(user.endDate).toLocaleDateString('fr-FR')}</span>
                                  </p>
                                </>
                              );
                            })() : (
                              <div className="inline-flex items-center h-8 px-4 rounded-xl bg-gray-100 text-gray-400 font-black text-[9px] uppercase tracking-widest">
                                 Non abonné
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="h-8 flex items-center">
                             <span className="text-[9px] font-black text-text/20 uppercase tracking-[0.2em] italic">Non applicable</span>
                          </div>
                        )}
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex justify-center">
                          <div className={`px-4 py-1.5 rounded-full flex items-center gap-2 border-2 ${
                            isEnabled 
                              ? 'border-[#10b981]/20 bg-[#10b981]/10 text-[#059669]' 
                              : 'border-[#f43f5e]/20 bg-[#f43f5e]/10 text-[#e11d48]'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full animate-pulse shadow-glow ${isEnabled ? 'bg-[#10b981] shadow-[#10b981]' : 'bg-[#f43f5e] shadow-[#f43f5e]'}`}></div>
                            <span className="text-[10px] font-black uppercase tracking-wider">
                              {status.text}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex justify-end items-center gap-2">
                          <button 
                            onClick={() => openView(user)}
                            className="group flex items-center gap-2 px-4 h-10 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 shadow-sm border border-blue-100"
                          >
                            <FontAwesomeIcon icon={faEye} className="text-[12px] group-hover:scale-110" />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden lg:block">Voir</span>
                          </button>

                          <button 
                            onClick={() => handleEditOpen(user)}
                            className="group flex items-center gap-2 px-4 h-10 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white transition-all duration-300 shadow-sm border border-amber-100"
                          >
                             <FontAwesomeIcon icon={faPen} className="text-[12px] group-hover:scale-110" />
                             <span className="text-[10px] font-black uppercase tracking-widest hidden lg:block">Éditer</span>
                          </button>

                          <button 
                            onClick={() => handleToggleStatus(user)}
                            className={`group flex items-center gap-2 px-4 h-10 rounded-xl transition-all duration-300 shadow-sm border ${
                              isEnabled 
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white' 
                              : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-600 hover:text-white'
                            }`}
                          >
                             <FontAwesomeIcon icon={isEnabled ? faCheckCircle : faTimesCircle} className="text-[12px] group-hover:scale-110" />
                             <span className="text-[10px] font-black uppercase tracking-widest hidden lg:block">
                               {isEnabled ? "Désactiver" : "Activer"}
                             </span>
                          </button>

                          <button 
                            onClick={() => handleDeleteOpen(user)}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all duration-300 shadow-sm border border-rose-100"
                          >
                             <FontAwesomeIcon icon={faTrash} className="text-[12px] group-hover:scale-110" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Section - High Fidelity */}
          <div className="px-8 py-8 bg-[#f8fafc]/30 border-t border-gray-100/50 flex flex-col sm:flex-row items-center justify-between gap-6">
             <div className="flex items-center gap-3">
                <p className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em]">
                   Affichage <span className="text-text/70">{Math.min(paginatedUsers.length, usersPerPage)}</span> sur <span className="text-text/70">{totals.total}</span>
                </p>
             </div>
             
             <div className="flex items-center gap-2">
                <button
                   onClick={() => setCurrentPage(1)}
                   disabled={currentPage === 1}
                   className="w-10 h-10 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-text/40 hover:text-primary hover:border-primary/20 disabled:opacity-30 disabled:pointer-events-none transition-all shadow-sm"
                >
                   <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </button>
                <button
                   onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                   disabled={currentPage === 1}
                   className="h-10 px-4 rounded-2xl bg-white border border-gray-100 flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-text/40 hover:text-primary hover:border-primary/20 disabled:opacity-30 disabled:pointer-events-none transition-all shadow-sm"
                >
                   <FontAwesomeIcon icon={faAngleLeft} /> PRÉC.
                </button>
                
                <div className="flex items-center gap-1 mx-2">
                   {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      if (totalPages > 5) {
                         if (pageNum !== 1 && pageNum !== totalPages && Math.abs(pageNum - currentPage) > 1) {
                            if (pageNum === 2 || pageNum === totalPages - 1) return <span key={pageNum} className="text-text/20 mx-1">...</span>;
                            return null;
                         }
                      }
                      
                      return (
                         <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-10 h-10 rounded-2xl flex items-center justify-center text-[11px] font-black transition-all ${
                               currentPage === pageNum 
                               ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                               : 'bg-white border border-gray-100 text-text/40 hover:text-primary hover:bg-primary/5'
                            }`}
                         >
                            {pageNum}
                         </button>
                      );
                   })}
                </div>

                <button
                   onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                   disabled={currentPage === totalPages}
                   className="h-10 px-4 rounded-2xl bg-white border border-gray-100 flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-text/40 hover:text-primary hover:border-primary/20 disabled:opacity-30 disabled:pointer-events-none transition-all shadow-sm"
                >
                   SUIV. <FontAwesomeIcon icon={faAngleRight} />
                </button>
                <button
                   onClick={() => setCurrentPage(totalPages)}
                   disabled={currentPage === totalPages}
                   className="w-10 h-10 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-text/40 hover:text-primary hover:border-primary/20 disabled:opacity-30 disabled:pointer-events-none transition-all shadow-sm"
                >
                   <FontAwesomeIcon icon={faAngleDoubleRight} />
                </button>
             </div>
          </div>
        </div>
      </div>

      {/* User Details Drawer */}
      <Modal
        open={viewOpen}
        onClose={closeView}
        className="flex justify-end backdrop-blur-sm bg-title/20"
        closeAfterTransition
      >
        <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${viewOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          
          {/* Drawer Header & Profile Summary */}
          <div className="relative p-10 pb-12 overflow-hidden border-b border-gray-50 bg-gradient-to-br from-[#09745f] to-[#07b98e] flex-shrink-0">
            <div className="absolute top-0 right-0 w-64 h-64 -mt-20 -mr-20 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 -mb-16 -ml-16 bg-black/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10 flex flex-col items-center text-center">
               <div className="relative mb-6">
                  <Avatar className="w-24 h-24 rounded-[2rem] bg-white/20 backdrop-blur-md border-4 border-white/30 text-3xl font-black shadow-2xl">
                    {getUserInitials(selectedUser?.fullName)}
                  </Avatar>
                  <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-2xl border-4 border-[#07b98e] flex items-center justify-center shadow-lg ${selectedUser && getUserStatus(selectedUser as User) ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                    <FontAwesomeIcon icon={selectedUser && getUserStatus(selectedUser as User) ? faCheckCircle : faTimesCircle} className="text-[10px] text-white" />
                  </div>
               </div>
               
               <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-1.5 drop-shadow-lg">
                 {(selectedUser?.fullName || "Utilisateur").trim()}
               </h2>
               
               <div className="flex items-center gap-3">
                  <span className="px-5 py-1.5 rounded-full bg-black/20 backdrop-blur-md border border-white/10 text-[9px] font-black text-white uppercase tracking-[0.2em]">
                    {getRoleDisplay(getRoleName(selectedUser))}
                  </span>
                  <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase">ID: #{selectedUser?.id}</span>
               </div>
            </div>

            <button 
              onClick={closeView}
              className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center rounded-2xl bg-white/10 text-white hover:bg-white hover:text-primary transition-all duration-300 group z-20 cursor-pointer"
            >
              <FontAwesomeIcon icon={faAngleRight} className="text-xl group-hover:translate-x-1 transition-transform pointer-events-none" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar p-10 space-y-10 bg-white">
            
            {/* Section: Contact & Identity */}
            <section className="space-y-4">
              <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Coordonnées & Identité
              </h3>
              
              <div className="grid gap-4">
                {selectedUser?.email && (
                  <div className="p-6 bg-slate-50 border border-slate-100 rounded-[2rem] flex items-center gap-6 group hover:bg-white hover:shadow-xl hover:shadow-black/5 transition-all duration-500">
                    <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                      <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-text/30 uppercase tracking-widest mb-0.5">Adresse email</p>
                      <p className="text-sm font-black text-title">{selectedUser.email}</p>
                    </div>
                  </div>
                )}

                {selectedUser?.phoneNumber && (
                  <div className="p-6 bg-slate-50 border border-slate-100 rounded-[2rem] flex items-center gap-6 group hover:bg-white hover:shadow-xl hover:shadow-black/5 transition-all duration-500">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                      <FontAwesomeIcon icon={faPhone} className="text-xl" />
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-text/30 uppercase tracking-widest mb-0.5">Téléphone</p>
                      <p className="text-sm font-black text-title">{selectedUser.phoneNumber}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Section: Academic Info (Conditional) */}
            {(selectedUser?.educationLevel || getRoleName(selectedUser) === "ROLE_TEACHER") && (
              <section className="space-y-4">
                <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Profil Académique
                </h3>
                
                <div className="grid gap-4">
                  {selectedUser?.educationLevel && (
                    <div className="p-6 bg-slate-50 border border-slate-100 rounded-[2rem] flex items-center gap-6 group hover:bg-white hover:shadow-xl hover:shadow-black/5 transition-all duration-500">
                      <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
                        <FontAwesomeIcon icon={faGraduationCap} className="text-xl" />
                      </div>
                      <div>
                        <p className="text-[9px] font-black text-text/30 uppercase tracking-widest mb-0.5">Niveau d'éducation</p>
                        <p className="text-sm font-black text-title">{selectedUser.educationLevel.replace('_', ' ')}</p>
                      </div>
                    </div>
                  )}

                  {getRoleName(selectedUser) === "ROLE_TEACHER" && (
                    <div className="p-6 bg-slate-50 border border-slate-100 rounded-[2rem] flex items-center justify-center text-center group hover:bg-white hover:shadow-xl hover:shadow-black/5 transition-all duration-500">
                      <div>
                        <p className="text-[9px] font-black text-text/30 uppercase tracking-widest mb-2">Matières gérées</p>
                        <p className="text-4xl font-black text-primary">
                          {selectedUser?.subjectCount ?? (Array.isArray(selectedUser?.subjects) ? selectedUser?.subjects?.length : 0) ?? 0}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Special Section: Super Teacher Data */}
            {getRoleName(selectedUser) === "ROLE_SUPER_TEACHER" && (() => {
              const classesCreated = selectedUser?.classCount ?? (Array.isArray(selectedUser?.groups) ? selectedUser.groups.length : 0);
              const totalTeachers = selectedUser?.teacherCount ?? (Array.isArray(selectedUser?.teachers) ? selectedUser.teachers.length : 0);
              const totalStudents = selectedUser?.studentCount ?? (Array.isArray(selectedUser?.students) ? selectedUser.students.length : 0);
              const totalSubjects = selectedUser?.subjectCount ?? (Array.isArray(selectedUser?.subjects) ? selectedUser.subjects.length : 0);
              
              const summaryItems = [
                { label: "Classes", value: classesCreated, icon: faUsers, color: "text-blue-500", bg: "bg-blue-500/5" },
                { label: "Profs", value: totalTeachers, icon: faChalkboardTeacher, color: "text-purple-500", bg: "bg-purple-500/5" },
                { label: "Élèves", value: totalStudents, icon: faUserGraduate, color: "text-emerald-500", bg: "bg-emerald-500/5" },
                { label: "Matières", value: totalSubjects, icon: faGraduationCap, color: "text-amber-500", bg: "bg-amber-500/5" },
              ];

              return (
                <section className="space-y-6">
                  <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                    Statistiques d'établissement
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {summaryItems.map((item, idx) => (
                      <div key={idx} className={`p-6 rounded-[2.5rem] border border-slate-100 ${item.bg} flex flex-col items-center justify-center text-center gap-2`}>
                        <FontAwesomeIcon icon={item.icon} className={`text-xl ${item.color}`} />
                        <p className="text-[24px] font-black text-title leading-none">{item.value}</p>
                        <p className="text-[9px] font-black text-text/30 uppercase tracking-[0.2em]">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })()}

            {/* Status Section */}
            <section className="space-y-4">
               <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Statut du Système
              </h3>
              
              <div className={`p-8 rounded-[2.5rem] border transition-all duration-500 flex items-center justify-between ${selectedUser && getUserStatus(selectedUser as User) ? 'bg-emerald-50/50 border-emerald-100' : 'bg-rose-50/50 border-rose-100'}`}>
                <div>
                  <p className={`text-[11px] font-black uppercase tracking-widest mb-1 ${selectedUser && getUserStatus(selectedUser as User) ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {selectedUser && getUserStatus(selectedUser as User) ? 'Compte Activé' : 'Compte Suspendu'}
                  </p>
                  <p className="text-[10px] font-bold text-text/40">{selectedUser && getUserStatus(selectedUser as User) ? 'Accès illimité aux ressources' : 'Accès temporairement restreint'}</p>
                </div>
                <div className={`w-3 h-3 rounded-full animate-pulse ${selectedUser && getUserStatus(selectedUser as User) ? 'bg-emerald-500 shadow-[0_0_12px_emerald]' : 'bg-rose-500 shadow-[0_0_12px_rose]'}`}></div>
              </div>
            </section>

            <div className="pb-10"></div>
          </div>

          {/* Drawer Footer Actions */}
          <div className="p-10 bg-white border-t border-gray-100 flex items-center justify-center z-[100] flex-shrink-0">
            <button 
              onClick={closeView}
              className="w-full h-16 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.4em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4 group cursor-pointer relative z-[110]"
            >
              <span className="pointer-events-none">Fermer la fiche</span>
              <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500 pointer-events-none">
                 <FontAwesomeIcon icon={faAngleRight} className="text-[10px]" />
              </div>
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit User Drawer */}
      <Modal
        open={editOpen}
        onClose={handleEditClose}
        className="flex justify-end backdrop-blur-sm bg-title/20"
        closeAfterTransition
      >
        <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${editOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          
          {/* Drawer Header */}
          <div className="relative p-10 pb-12 overflow-hidden border-b border-gray-50 bg-gradient-to-br from-indigo-600 to-blue-500 flex-shrink-0">
            <div className="absolute top-0 right-0 w-64 h-64 -mt-20 -mr-20 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 -mb-16 -ml-16 bg-black/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10 flex flex-col items-center text-center">
               <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center mb-6 shadow-2xl group-hover:rotate-12 transition-transform duration-500">
                  <FontAwesomeIcon icon={faPen} className="text-3xl text-white" />
               </div>
               
               <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-1.5 drop-shadow-lg">
                 Modifier l'utilisateur
               </h2>
               <p className="text-[10px] font-bold text-white/60 tracking-widest uppercase">Mise à jour des informations système</p>
            </div>

            <button 
              onClick={handleEditClose}
              className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center rounded-2xl bg-white/10 text-white hover:bg-white hover:text-primary transition-all duration-300 group z-20 cursor-pointer"
            >
              <FontAwesomeIcon icon={faAngleRight} className="text-xl group-hover:translate-x-1 transition-transform pointer-events-none" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar p-10 space-y-10 bg-white">
            
            <div className="space-y-8">
               {/* Identity Card (Read Only) */}
               <div className="p-8 bg-slate-50 border border-slate-100 rounded-[2.5rem] space-y-6">
                  <div className="flex items-center gap-4 border-b border-slate-200 pb-6">
                     <Avatar className="w-14 h-14 rounded-xl bg-primary/10 text-primary font-black">
                        {getUserInitials(selectedUser?.fullName)}
                     </Avatar>
                     <div>
                        <p className="text-[13px] font-black text-title uppercase">{selectedUser?.fullName}</p>
                        <p className="text-[9px] font-bold text-text/40">ID: #{selectedUser?.id}</p>
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="text-[9px] font-black text-text/30 uppercase tracking-widest mb-2 block">Rôle Actuel</label>
                        <div className="h-12 flex items-center px-4 bg-white rounded-xl border border-slate-200 text-[11px] font-black text-title uppercase italic opacity-60">
                           {getRoleDisplay(getRoleName(selectedUser))}
                        </div>
                     </div>
                     <div>
                        <label className="text-[9px] font-black text-text/30 uppercase tracking-widest mb-2 block">Email (Fixe)</label>
                        <div className="h-12 flex items-center px-4 bg-white rounded-xl border border-slate-200 text-[11px] font-black text-title lowercase opacity-60 overflow-hidden text-ellipsis">
                           {selectedUser?.email}
                        </div>
                     </div>
                  </div>
               </div>

               {/* Editable Fields */}
               <section className="space-y-6">
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-title uppercase tracking-[0.2em] pl-2">Téléphone professionnel</label>
                     <div className="relative group">
                        <div className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-text/30 group-focus-within:text-primary group-focus-within:bg-primary/5 transition-all">
                           <FontAwesomeIcon icon={faPhone} />
                        </div>
                        <input 
                           type="tel"
                           value={form.phoneNumber || ""}
                           onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                           placeholder="Numéro de téléphone..."
                           className="w-full h-16 pl-20 pr-6 bg-slate-50 border border-slate-100 rounded-2xl font-black text-sm text-title outline-none focus:bg-white focus:border-primary/20 focus:shadow-xl focus:shadow-black/5 transition-all"
                        />
                     </div>
                  </div>

                  {(() => {
                    const userRole = getRoleName(selectedUser);
                    if (userRole === "ROLE_TEACHER" || userRole === "ROLE_SUPER_TEACHER") return null;
                    return (
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-title uppercase tracking-[0.2em] pl-2">Niveau d'éducation</label>
                        <div className="relative group">
                           <div className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-text/30 group-focus-within:text-primary group-focus-within:bg-primary/5 transition-all pointer-events-none">
                              <FontAwesomeIcon icon={faGraduationCap} />
                           </div>
                           <select 
                              value={form.educationLevel || ""}
                              onChange={e => setForm({ ...form, educationLevel: e.target.value })}
                              className="w-full h-16 pl-20 pr-6 bg-slate-50 border border-slate-100 rounded-2xl font-black text-sm text-title outline-none appearance-none focus:bg-white focus:border-primary/20 focus:shadow-xl focus:shadow-black/5 transition-all cursor-pointer"
                           >
                              <option value="">Sélectionner un niveau...</option>
                              {classesLevel.map((level) => (
                                 <option key={level.value} value={level.value}>{level.label}</option>
                              ))}
                           </select>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="space-y-4 pt-4">
                     <label className="text-[10px] font-black text-title uppercase tracking-[0.2em] pl-2">Contrôle d'accès</label>
                     <button
                        onClick={() => setForm({ ...form, isEnabled: !form.isEnabled })}
                        className={`w-full p-6 rounded-[2rem] border transition-all duration-500 flex items-center justify-between group ${form.isEnabled ? 'bg-emerald-50/50 border-emerald-100' : 'bg-rose-50/50 border-rose-100'}`}
                     >
                        <div className="flex items-center gap-6">
                           <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${form.isEnabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' : 'bg-rose-500 text-white shadow-lg shadow-rose-200'}`}>
                              <FontAwesomeIcon icon={form.isEnabled ? faCheckCircle : faTimesCircle} className="text-xl" />
                           </div>
                           <div className="text-left">
                              <p className={`text-[11px] font-black uppercase tracking-widest ${form.isEnabled ? 'text-emerald-700' : 'text-rose-700'}`}>
                                 {form.isEnabled ? 'Compte Actif' : 'Compte Bloqué'}
                              </p>
                              <p className="text-[10px] font-bold text-text/40">Cliquez pour basculer le statut</p>
                           </div>
                        </div>
                        <div className={`w-14 h-8 rounded-full relative transition-colors duration-500 ${form.isEnabled ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                           <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-all duration-500 ${form.isEnabled ? 'left-7' : 'left-1'}`}></div>
                        </div>
                     </button>
                  </div>
               </section>
            </div>

            <div className="pb-10"></div>
          </div>

          {/* Drawer Footer Actions */}
          <div className="p-10 bg-white border-t border-gray-100 flex items-center gap-4 z-40 flex-shrink-0">
            <button 
              onClick={handleEditClose}
              className="flex-1 h-16 bg-slate-50 text-text/40 rounded-2xl font-black text-[11px] uppercase tracking-[0.4em] hover:bg-slate-100 active:scale-95 transition-all duration-300 cursor-pointer"
            >
              <span className="pointer-events-none">Annuler</span>
            </button>
            <button 
              onClick={handleUpdate}
              className="flex-[2] h-16 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.4em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4 group cursor-pointer"
            >
              <span className="pointer-events-none">Sauvegarder</span>
              <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 pointer-events-none">
                 <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
              </div>
            </button>
          </div>
        </div>
      </Modal>

      <AnimatePresence>
        {deleteOpen && (
          <Modal
            open={deleteOpen}
            onClose={handleDeleteClose}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-[450px] bg-white rounded-[3rem] p-10 shadow-3xl text-center outline-none border border-white/20"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="w-24 h-24 rounded-[2.5rem] bg-rose-50 text-rose-500 flex items-center justify-center text-4xl mx-auto mb-8 border border-rose-100 shadow-sm relative">
                <div className="absolute inset-0 bg-rose-400/20 rounded-[2.5rem] animate-ping opacity-50"></div>
                <FontAwesomeIcon icon={faTrashCan} className="relative z-10" />
              </div>
              
              <h3 className="text-2xl font-black text-title uppercase tracking-tight mb-4 text-center">
                Supprimer <span className="text-rose-500">l'Utilisateur</span>
              </h3>
              
              <p className="text-sm font-medium text-text/60 leading-relaxed mb-10 px-4 text-center">
                Voulez-vous supprimer définitivement l'utilisateur <span className="text-[#05322B] font-black">{selectedUser?.fullName}</span> ? cette action est irréversible.
              </p>

              <div className="flex gap-4">
                <button
                  className="flex-1 h-16 bg-slate-50 text-text/40 font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-slate-100 transition-all duration-300 outline-none cursor-pointer"
                  onClick={handleDeleteClose}
                >
                  Annuler
                </button>
                <button
                  className="flex-1 h-16 bg-rose-500 text-white font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl shadow-rose-500/20 hover:bg-rose-600 hover:-translate-y-1 transition-all duration-300 outline-none cursor-pointer"
                  onClick={handleDelete}
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
    );
};

export default ManagementUsers;








